import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {showNotification} from 'app/toasts/slice';
import {
  INVITATION_API_RESEND_URL,
  INVITATION_API_URL,
  USER_API_URL,
  USER_BULK_REMOVE_API_URL,
  USER_SET_READONLY_API_URL,
} from 'app/urls';
import {
  BulkRemoveUsersParams,
  InvitationQueryParamsType,
  NewInvitationType,
  NewUserType,
  UserPermissionsFetchParamsType,
  UsersFetchParamsType,
  UsersLocationParamsType,
} from 'app/user/types';
import {gt, ngt} from 'app/utils/i18n';
import {INVITATION_RTK_TAG, TEAMTAG, USER_RTK_TAG} from 'app/utils/rtk-tags';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import type {UserPermissionsSerializerType} from '../current-user/types';
import type {
  CompletedUserJobSerializedType,
  InvitationSerializedType,
  ListResponseType,
  UserJobSerializedType,
  UserSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [USER_RTK_TAG, TEAMTAG, INVITATION_RTK_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      users: build.query<
        ListResponseType<UserSerializedType>,
        UsersFetchParamsType | UsersLocationParamsType
      >({
        query: (params) => ({
          url: USER_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(USER_RTK_TAG),
        onQueryStarted: showMessages({
          onError: () => gt('There was an error fetching the members'),
        }),
      }),
      userDetails: build.query<UserSerializedType, {id?: number | string}>({
        query: ({id}) => ({
          url: `${USER_API_URL}${id}/`,
        }),
        providesTags: (response) =>
          response != null ? [{type: USER_RTK_TAG, id: response.url}] : [],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('There was an error fetching member details'),
          })(args, api);
          showLoadingBar(args, api);
        },
      }),
      userPermissions: build.query<
        ListResponseType<UserPermissionsSerializerType>,
        UserPermissionsFetchParamsType
      >({
        query: (params) => ({
          url: `${USER_API_URL}permissions/`,
          params,
        }),
      }),
      updateUser: build.mutation<
        UserSerializedType,
        {
          id: number;
        } & Partial<
          Pick<
            UserSerializedType,
            // adds as you need
            | 'url'
            | 'restrict_saml_only_login'
            | 'is_super_teammate'
            | 'is_super_manager'
            | 'is_billing_admin'
            | 'name'
            | 'email'
            | 'is_guest_until'
          >
        >
      >({
        query: ({id, ...body}) => ({
          url: `${USER_API_URL}${id}/`,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Member details updated.'),
          onError: {
            fallback: () => gt('Unable to update member details.'),
            byStatus: {
              403: (error) => error?.detail,
            },
          },
        }),
        invalidatesTags: (response, errors, {url}) => [
          {type: USER_RTK_TAG, id: 'list'},
          {type: USER_RTK_TAG, id: url},
        ],
      }),
      resendUserInvitation: build.mutation<
        unknown,
        {to_user_id: number; message?: string | null}
      >({
        query: (body) => ({
          url: INVITATION_API_RESEND_URL,
          method: 'POST',
          body: {send_mail: true, ...body},
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('The invitation was re-sent.'),
          onError: {
            byStatus: {403: (error) => error?.detail},
            fallback: () => gt('Unable to re-send invitation.'),
          },
        }),
      }),
      createUser: build.mutation<UserSerializedType, NewUserType>({
        query: (user: NewUserType) => ({
          url: USER_API_URL,
          method: 'POST',
          body: user,
        }),
        invalidatesTags: () => [{type: USER_RTK_TAG, id: 'list'}],
        onQueryStarted: async (args, api) => {
          const {queryFulfilled} = api;
          let userData: UserSerializedType;

          try {
            userData = (await queryFulfilled)?.data;
          } catch {
            // continue regardless of error, because showMessages will handle it
          }

          showMessages({
            onSuccess: () =>
              userData.is_guest_until == null
                ? gt('Added the member %s.', userData.name)
                : gt('%s was added to your account as a guest.', userData.name),
            onError: {
              byStatus: {
                403: (error) => error?.detail,
                400: (error) => error?.non_field_errors?.[0],
              },
              fallback: () => gt('Unable to add member.'),
            },
          })(args, api);
        },
      }),
      removeUser: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed member.'),
          onError: {
            byStatus: {403: (error) => error?.detail},
            fallback: () => gt('Unable to remove member.'),
          },
        }),
        invalidatesTags: () => [{type: USER_RTK_TAG, id: 'list'}],
      }),
      bulkRemoveUsers: build.mutation<
        CompletedUserJobSerializedType,
        BulkRemoveUsersParams
      >({
        queryFn: (
          {users},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: USER_BULK_REMOVE_API_URL,
                    method: 'POST',
                    body: {users},
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        onQueryStarted: ({users, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt('Removing member...', 'Removing members...', users.length),
            onSuccess: () => [
              ngt('Removed member.', 'Removed members.', users.length),
              {level: 'success'},
            ],
            onError: {
              byStatus: {
                403: (error) => error?.detail,
                400: (error) => error?.non_field_errors?.[0],
              },
              fallback: () =>
                ngt(
                  'Unable to remove member.',
                  'Unable to remove members.',
                  users.length
                ),
            },
          })(args, api);
        },
        invalidatesTags: () => [{type: USER_RTK_TAG, id: 'list'}],
      }),
      inviteUser: build.mutation<
        unknown,
        {invitations: Array<NewInvitationType>; syncThreshold?: number}
      >({
        queryFn: ({invitations}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: INVITATION_API_URL,
            method: 'POST',
            body: invitations,
          });
        },
        invalidatesTags: () => [
          {type: USER_RTK_TAG, id: 'list'},
          {type: TEAMTAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          const {dispatch} = api;
          const {invitations, syncThreshold = 5} = args;

          if (syncThreshold && invitations.length > syncThreshold) {
            const extraTimeMessage =
              invitations.length >= 200
                ? ` ${gt('It may take some minutes.')}`
                : invitations.length >= 50
                ? ` ${gt('It may take some seconds.')}`
                : '';

            dispatch(
              showNotification(
                `${ngt(
                  'Sending invitation...',
                  'Sending invitations...',
                  invitations.length
                )}${extraTimeMessage}`
              )
            );
          }

          showMessages({
            onSuccess: ({invitations}) =>
              ngt('Invitation sent.', 'Invitations sent.', invitations.length),
            onError: {
              byStatus: {403: (error) => error?.detail},
              fallback: () =>
                ngt(
                  'Unable to send invitation to the user at the moment.',
                  'Unable to send those invitations at the moment.',
                  invitations.length
                ),
            },
          })(args, api);
        },
      }),
      setReadonlyUser: build.mutation<unknown, {url: string}>({
        query: (body) => ({
          body,
          url: USER_SET_READONLY_API_URL,
          method: 'POST',
        }),
        invalidatesTags: () => [
          {type: USER_RTK_TAG, id: 'list'},
          {type: TEAMTAG, id: 'list'},
        ],
        onQueryStarted: showMessages({
          onSuccess: () =>
            gt("Access set to read only for this member's teams"),
          onError: {
            byStatus: {403: (error) => error?.detail},
            fallback: () => gt('Unable to set access level at the moment.'),
          },
        }),
      }),
      invitations: build.query<
        ListResponseType<InvitationSerializedType>,
        InvitationQueryParamsType
      >({
        query: (params) => ({
          params,
          method: 'GET',
          url: INVITATION_API_URL,
        }),
        providesTags: [{type: INVITATION_RTK_TAG, id: 'list'}],
        onQueryStarted: showMessages({
          onError: () => gt('There was an error fetching the invitations'),
        }),
      }),
      deleteInvitation: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          method: 'DELETE',
          url,
        }),
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: USER_RTK_TAG, id: 'list'},
          {type: INVITATION_RTK_TAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onSuccess: () => gt('The invitation was revoked.'),
            onError: {
              fallback: () => gt('Unable to revoke invitation.'),
              byStatus: {403: (error) => error?.detail},
            },
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useUpdateUserMutation: useUpdateUser,
  useResendUserInvitationMutation: useResendUserInvitation,
  useSetReadonlyUserMutation: useSetReadonlyUser,
  useRemoveUserMutation: useRemoveUser,
  useBulkRemoveUsersMutation: useBulkRemoveUsers,
  useCreateUserMutation: useCreateUser,
  useInviteUserMutation: useInviteUser,
  useLazyUsersQuery: useLazyUsersQuery,
  useDeleteInvitationMutation: useDeleteInvitation,
  useInvitationsQuery: useGetInvitations,
  useUserPermissionsQuery: useUserPermissions,
  useLazyUserPermissionsQuery: useLazyUserPermissions,
  useUsersQuery: useUsers,
  useUserDetailsQuery: useUserDetails,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: UsersLocationParamsType
): UsersFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
  search: query.search ? query.search : undefined,
  ordering: query.ordering || 'first_name,last_name',
  team_id: query.team_id,
  exclude_team_id: query.exclude_team_id,
  include_shadow_user: query.include_shadow_user,
  id: query.id != null ? query.id : undefined,
  exclude_id: query.exclude_id,
  status: query.status,
  account_role: query.account_role,
  in_no_team_roles: query.in_no_team_roles,
  exclude_role: query.exclude_role,
  exclude_guests: query.exclude_guests,
  content_type: query.content_type,
  object_id: query.object_id,
  with_permissions: query.with_permissions,
});
